import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAdmin } from "@/admin/AdminContext";
import { PageHeader, StatusPill, EmptyState } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LifeBuoy, Send, CheckCircle2 } from "lucide-react";

export default function Support() {
  const { version } = useAdmin();
  const [tickets, setTickets] = useState([]);
  const [replyText, setReplyText] = useState({});

  const load = () => api.get("/support/tickets").then((r) => setTickets(r.data)).catch(() => {});
  useEffect(() => { load(); }, [version]);

  const sendReply = async (id) => {
    if (!replyText[id]) return;
    try {
      await api.post(`/support/tickets/${id}/reply`, { message: replyText[id] });
      setReplyText({ ...replyText, [id]: "" });
      toast.success("Reply sent");
      load();
    } catch (e) { toast.error("Could not send reply"); }
  };

  const resolve = async (id) => {
    try { await api.post(`/support/tickets/${id}/status`, { status: "resolved" }); load(); }
    catch (e) { toast.error("Could not update"); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Support" subtitle="Client messages and requests" />
      {tickets.length === 0 ? (
        <EmptyState icon={LifeBuoy} title="No support tickets" hint="Messages from clients will appear here." />
      ) : (
        <div className="space-y-4" data-testid="tickets-list">
          {tickets.map((t) => (
            <div key={t.ticket_id} data-testid={`ticket-${t.ticket_id}`} className="bg-white border border-[#E2EBF0] rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[#0D3B2D]">{t.subject}</span>
                    <StatusPill status={t.status} />
                  </div>
                  <div className="text-xs text-[#4A635A] mt-0.5">{t.client_name} · {new Date(t.created_at).toLocaleString()}</div>
                </div>
                {t.status === "open" && (
                  <Button onClick={() => resolve(t.ticket_id)} variant="outline" size="sm"
                    data-testid={`resolve-${t.ticket_id}`} className="border-[#BCECD8] text-[#10B981] rounded-lg text-xs">
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Resolve
                  </Button>
                )}
              </div>
              <p className="text-sm text-[#0D3B2D]/80 mt-3 bg-[#F8FAF9] rounded-lg p-3">{t.message}</p>
              {t.replies?.map((r, i) => (
                <p key={i} className="text-sm text-[#0D3B2D] mt-2 bg-[#F4FFF9] border border-[#BCECD8] rounded-lg p-3">
                  <span className="font-mono text-xs text-[#10B981]">{r.author} replied:</span><br />{r.message}
                </p>
              ))}
              <div className="flex gap-2 mt-3">
                <Textarea value={replyText[t.ticket_id] || ""} onChange={(e) => setReplyText({ ...replyText, [t.ticket_id]: e.target.value })}
                  placeholder="Write a reply…" rows={1} data-testid={`reply-input-${t.ticket_id}`}
                  className="bg-white border-[#E2EBF0] text-sm min-h-0" />
                <Button onClick={() => sendReply(t.ticket_id)} data-testid={`send-reply-${t.ticket_id}`}
                  className="bg-[#0D3B2D] hover:bg-[#071A13] text-white rounded-lg shrink-0"><Send className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
